import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { FormikProvider, useFormik } from 'formik'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useContext } from 'react'
import { useToggle } from 'react-use'
import * as yup from 'yup'

import agent from 'agent'
import PrivateNetworkIntegrationCheckbox from 'components/rbac-settings/teams/PrivateNetworkIntegrationCheckbox'
import TestStatusCheck from 'components/rbac-settings/teams/TestStatusCheck'
import TestTeamExternalIntegration from 'components/rbac-settings/teams/TestTeamExternalIntegration'
import { EditGoldenSignalContext } from 'components/services/create/EditGoldenSignalContext'
import ServiceGoldenSignalHeaders from 'components/services/create/ServiceGoldenSignalHeaders'
import { TestEvaluationGoldenSignalIntegration } from 'components/services/create/TestEvaluationGoldenSignalIntegration'
import { getResourceNameFromResponseBody } from 'components/services/create/TestEvaluationResultsGoldenSignalIntegration'
import { MAX_GOLDEN_SIGNAL_NAME_LENGTH } from 'components/services/service-utils'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { EndpointConfiguration, EndpointResponse } from 'types/api/end-point'
import { EvaluationConfiguration } from 'types/api/services/evaluation'
import {
  authenticationStatus,
  TeamExternalIntegration,
  ServiceGoldenSignal
} from 'types/api/services/golden-signal.d'
import { StatusCheckCreateRequest } from 'types/api/services/status'
import { formatResponseBodyEvaluationPredicatesGrafana } from 'utils/status-checks/status-check-integrations-util-grafana'
import { messageFromResponseError } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  computeResponseBodyEvaluation,
  convertToApiUrl,
  convertToBaseUrl,
  customStatusCheckIntegration,
  defaultStatusCheckEvaluationConfiguration,
  encodeQueryParams,
  formatResponseBodyEvaluationPredicates,
  isValidStatusCodeOrRange,
  prepareHeaders
} from 'utils/team-external-integrations/team-external-integrations-util'
import { DOMAIN_NAME_INTEGRATIONS, ENDPOINT_URLS } from 'utils/third-party'
import { generateGoldenSignalIntegrationValidationSchema } from 'utils/yup-additions'

import { SelectTeamExternalIntegration } from './SelectTeamExternalIntegration'
import { URLHelperText } from './URLHelperText'

export const NameField = ({ form, isEditMode }) => (
  <TextField
    data-testid="service-status-check-name"
    disabled={isEditMode}
    error={form.touched?.name && Boolean(form.errors.name)}
    helperText={form.touched?.name && form.errors.name}
    inputProps={{ maxLength: MAX_GOLDEN_SIGNAL_NAME_LENGTH }}
    label="Name"
    name="name"
    type="text"
    value={form.values.name}
    onBlur={form.handleBlur}
    onChange={form.handleChange}
  />
)

export const SaveServiceGoldenSignalForm: React.FC<{
  goldenSignal?: ServiceGoldenSignal
  onAuthSave: () => void
  onCanceled: () => void
  onChanged: (statusCheck: ServiceGoldenSignal, isEdit: boolean) => void
}> = ({ goldenSignal, onAuthSave, onCanceled, onChanged }) => {
  const {
    privateNetworkIntegrationAlways,
    privateNetworkIntegration: isPrivateNetworkIntegrationEnabled
  } = useFlags()

  const [endpointResponse, setEndpointResponse] =
    React.useState<EndpointResponse>()

  const { currentTeamId } = useAuth()
  const [isEvaluationChecked, setIsEvaluationChecked] = useToggle(false)
  const { integration, reFetchExternalIntegrations, goldenSignals } =
    useContext(EditGoldenSignalContext)

  const isEditMode = Boolean(goldenSignal)
  const duplicateGoldenSignalName =
    goldenSignal && !goldenSignal.id ? goldenSignal.name : '' // if golden signal exists but does not have an id, it means it's a duplicate
  const isNoIntegrationAvailable = !integration && !goldenSignal?.integration

  const [resourceName, setResourceName] = React.useState<string>()
  const existingIntegrationNames = goldenSignals.map((gs) => gs.name)

  const shouldEvalExistingGoldenSignals = (): boolean =>
    !isEditMode && form.values.integration.type !== IntegrationType.Datadog

  /**
   * We will be annotating the golden signal integration names in the view with the type of
   * integration. This will help us avoid naming displays like: Datadog (Datadog).
   * It's not necessary with all integration types.
   */
  const reservedNames = ['datadog', 'other', 'grafana cloud']

  const generateGoldenSignalValidationSchema = (values) =>
    yup.object().shape({
      integration: yup.object().concat(
        generateGoldenSignalIntegrationValidationSchema(
          goldenSignals.filter((gs) => gs.id !== goldenSignal?.id),
          isEditMode && goldenSignal.integration.isConfigured,
          isEditMode
            ? goldenSignal.integration?.type
            : values?.integration?.type ?? IntegrationType.Custom
        )
      ),
      isContinuous: yup.boolean(),
      name: yup
        .string()
        .required('Please enter the Golden Signal name')
        .min(3, 'Please enter the Golden Signal name')
        .max(MAX_GOLDEN_SIGNAL_NAME_LENGTH, 'Golden Signal name is too long')
        .notOneOf(
          shouldEvalExistingGoldenSignals() ? existingIntegrationNames : [],
          'Golden Signal name already exists'
        )
        .test(
          'is-name-not-reserved',
          'Please do not use reserved words',
          (value) => {
            return !reservedNames.includes(value?.trim().toLowerCase())
          }
        ),
      evaluationConfiguration: yup
        .object()
        .shape({
          healthyStatusCode: yup
            .string()
            .test(
              'is-healthy-status-code-valid',
              'Use a single status code (200) or a range (200-299)',
              (value) => isValidStatusCodeOrRange(value)
            )
            .required('Status Code is required'),
          requestTimeout: yup
            .number()
            .min(50, 'Request timeout must be between 50 and 5000')
            .max(5000, 'Request timeout must be between 50 and 5000')
            .required('Request timeout is required'),
          responseBodyEvaluation: yup.object().shape({
            predicates: yup.array().of(
              yup.object().shape({
                jpQuery: yup.string().required('Key is required'),
                type: yup.string().required('Type is required'),
                comparator: yup.string().required('Comparator is required'),
                rValue: yup.string().required('Value is required')
              })
            )
          })
        })
        .notRequired()
    })

  const headers =
    isEditMode && goldenSignal?.integration?.headers?.length > 0
      ? goldenSignal.integration?.headers
      : integration?.headers || []

  const handleGoldenSignalSubmit = async () => {
    const { integration, name, evaluationConfiguration, isContinuous } =
      form.values
    try {
      const request: StatusCheckCreateRequest = {
        isContinuous: isContinuous,
        isPrivateNetwork: integration.isPrivateNetworkIntegration,
        name: name,
        thirdPartyPresets: integration.type,
        endpointConfiguration: {
          url: encodeQueryParams(
            await convertToApiUrl(integration, currentTeamId)
          ),
          ...(integration.type === IntegrationType.Custom && {
            headers: prepareHeaders(integration)
          })
        } as EndpointConfiguration,
        evaluationConfiguration: {
          okLatencyMaxMs: evaluationConfiguration.requestTimeout,
          okStatusCodes: [evaluationConfiguration.healthyStatusCode],
          responseBodyEvaluation:
            integration.type === IntegrationType.Custom
              ? formatResponseBodyEvaluationPredicates(
                  evaluationConfiguration.responseBodyEvaluation
                )
              : integration.type === IntegrationType.Grafana
              ? formatResponseBodyEvaluationPredicatesGrafana(
                  await convertToApiUrl(integration, currentTeamId)
                )
              : computeResponseBodyEvaluation(integration)
        } as EvaluationConfiguration,
        ...(integration.isConfigured && {
          teamExternalIntegration: {
            name: integration.name,
            type: integration.type?.toUpperCase()
          }
        }),
        ...(!integration.isConfigured &&
          goldenSignal?.id && {
            webhookId: goldenSignal.id
          })
      }

      const isCustomIntegration = integration.type === IntegrationType.Custom

      /**
       * custom integration needs to be configured
       * on the team external integration level first before
       * we can add this object to the request body
       */
      if (
        (isCustomIntegration && integration.isConfigured) ||
        (!isCustomIntegration && !integration.isConfigured)
      ) {
        request.teamExternalIntegration = {
          name: integration.name,
          type: integration.type?.toUpperCase()
        }
      }

      let id
      if (isEditMode && goldenSignal.id) {
        await agent.put(
          `status-checks/${goldenSignal.id}?teamId=${currentTeamId}`,
          request
        )
        id = goldenSignal.id
      } else {
        const { data: postData } = await agent.post(
          `status-checks?teamId=${currentTeamId}`,
          request
        )
        id = postData
      }

      if (form.isSubmitting) {
        toast({ type: 'success', text: 'Golden Signal saved successfully.' })
        onChanged({ ...form.values, id: id } as ServiceGoldenSignal, isEditMode)
      }
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an error while saving the Golden Signal. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  const form = useFormik<ServiceGoldenSignal>({
    initialValues: {
      isContinuous: isEditMode
        ? goldenSignal.isContinuous
        : !isNoIntegrationAvailable,
      integration: {
        isPrivateNetworkIntegration: false,
        ...(isEditMode
          ? { ...goldenSignal.integration }
          : integration
          ? integration
          : customStatusCheckIntegration),
        url: isEditMode
          ? goldenSignal.integration?.url
          : integration?.url || '',
        ...((integration?.type === IntegrationType.Custom ||
          isNoIntegrationAvailable) && {
          headers: headers
        }),
        type: isEditMode
          ? goldenSignal?.integration?.type
          : integration?.type ?? IntegrationType.Custom
      },
      name: isEditMode ? goldenSignal?.name : '',
      evaluationConfiguration: isEditMode
        ? { ...goldenSignal?.evaluationConfiguration }
        : defaultStatusCheckEvaluationConfiguration
    },
    validateOnMount: true,
    onSubmit: handleGoldenSignalSubmit,
    validationSchema: () =>
      yup.lazy((values) => generateGoldenSignalValidationSchema(values))
  })

  const formRef = React.useRef(form)
  React.useEffect(() => {
    formRef.current.resetForm()
    setEndpointResponse(null)
  }, [integration?.type])

  /**
   * Calculates if the current GS being edited already has an existing url configured
   */
  const integrationUrlExists = isEditMode
    ? goldenSignals.filter(
        (gs) => gs.integration.url === goldenSignal?.integration?.url
      ).length > 0
    : goldenSignals.filter((gs) => gs.integration.url === integration?.url)
        .length > 0

  const touched = form.touched
  const errors = form.errors

  const computeURLabel = (integrationType: IntegrationType) => {
    switch (integrationType) {
      case IntegrationType.Datadog:
        return 'Monitor URL'
      case IntegrationType.Pagerduty:
        return 'PagerDuty Incident or Service ID URL'
      case IntegrationType.Newrelic:
        return 'New Relic API URL'
      case IntegrationType.Grafana:
        return 'Alert Rule URL'
      case IntegrationType.Dynatrace:
        return 'Entity URL'
      case IntegrationType.Appdynamics:
        return 'Application URL'
      default:
        return 'Endpoint URL'
    }
  }

  const [isAuthenticationSaving, setIsAuthenticationSaving] = useToggle(false)
  const [isAuthenticationSaved, setIsAuthenticationSaved] = useToggle(false)
  const [authenticationError, setAuthenticationError] = React.useState('')

  const resetAuthenticationStates = () => {
    setIsAuthenticationSaving(false)
    setIsAuthenticationSaved(false)
    setAuthenticationError('')
  }

  const onTeamExternalIntegrationChanged = (
    integration: TeamExternalIntegration
  ) => {
    form.setValues({
      ...form.values,
      integration: integration
    })
    setEndpointResponse(null)
    setIsEvaluationChecked(false)
    resetAuthenticationStates()
  }

  const handleSaveAuthentication = async () => {
    const { integration } = form.values

    const isCustomIntegration = integration.type === IntegrationType.Custom
    const isDomainNameIntegration = DOMAIN_NAME_INTEGRATIONS.includes(
      integration.type
    )

    try {
      resetAuthenticationStates()

      setIsAuthenticationSaving(true)

      await agent.post(
        `external-integrations/status-check?type=${integration.type.toUpperCase()}&teamId=${currentTeamId}`,
        {
          url: isCustomIntegration
            ? integration.url
            : isDomainNameIntegration
            ? convertToBaseUrl(integration)
            : ENDPOINT_URLS[integration.type],
          headers: prepareHeaders(integration),
          name: integration.name,
          privateNetwork: integration.isPrivateNetworkIntegration,
          lastAuthenticationStatus: authenticationStatus.Authenticated
        }
      )
      reFetchExternalIntegrations()

      // save auth to this golden signal if we are editing
      if (isEditMode) {
        await handleGoldenSignalSubmit()
      }

      onAuthSave()
      setIsAuthenticationSaved(true)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an error while saving the Golden Signal. Please try again later.'
      )
      setAuthenticationError(error)
    } finally {
      setIsAuthenticationSaving(false)
    }
  }

  const isServiceGoldenSignalHeadersSaveButtonDisabled =
    isAuthenticationSaving ||
    Boolean(form.errors?.integration?.headers) ||
    form.values.integration?.lastAuthenticationStatus !==
      authenticationStatus.Authenticated ||
    !form.dirty

  const isTestStatusCheckButtonDisabledBase =
    Boolean(form.errors?.integration?.url) ||
    Boolean(!form.values?.integration?.url)

  const isTestStatusCheckButtonDisabledEnhanced =
    isTestStatusCheckButtonDisabledBase ||
    Boolean(authenticationError) ||
    !isAuthenticationSaved

  const isTestStatusCheckButtonDisabled = form.values.integration.isConfigured
    ? isTestStatusCheckButtonDisabledBase
    : isTestStatusCheckButtonDisabledEnhanced

  const isSaveButtonDisabled =
    !form.isValid ||
    form.isSubmitting ||
    !isValidStatusCodeOrRange(endpointResponse?.httpStatusCode) ||
    !isEvaluationChecked ||
    Boolean(authenticationError)

  /**
   * we don't want to show the save authentication button
   * when the integration is custom and not configured
   * because we need the team integration to exist (configured) first
   * before we know where to save the auth to
   */
  const shouldShowAuthenticationButton =
    (form.values.integration?.type === IntegrationType.Custom &&
      integration?.isConfigured &&
      !isAuthenticationSaved) ||
    (form.values.integration?.type !== IntegrationType.Custom &&
      !integration?.isConfigured &&
      !isAuthenticationSaved)

  /**
   * If using a new Custom GS, this will not throw an immediate error to the user
   * until they update the initial value.
   */
  const resolveTouchState = integrationUrlExists
    ? true
    : touched?.integration?.url

  return (
    <FormikProvider value={form}>
      <Stack
        data-test-save-service-golden-signal-form
        alignItems="flex-start"
        p={5}
        spacing={2}
        sx={{ minWidth: '50vw' }}
      >
        <Typography
          data-testid="service-status-check-header"
          pb={2}
          variant="h5"
        >
          {isEditMode && goldenSignal.id ? 'Edit ' : 'Add '} Golden Signal -{' '}
          {form.values?.integration?.name}
        </Typography>

        {isNoIntegrationAvailable && (
          <SelectTeamExternalIntegration
            integrationName={form.values?.integration?.name}
            onChanged={onTeamExternalIntegrationChanged}
          />
        )}

        {form.values.integration?.name === 'Other' &&
          isNoIntegrationAvailable && (
            <FormGroup data-test-save-service-golden-signal-form-is-continuous>
              <FormLabel component="legend">Continuous Golden Signal</FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.values.isContinuous}
                    name="isContinuous"
                    onChange={form.handleChange}
                  />
                }
                label="Allow this Golden Signal to run continuously during your scenario (polling every 10 seconds)"
              />
            </FormGroup>
          )}
        {(form.values.integration?.type?.toLowerCase() ===
          IntegrationType.Custom ||
          (isEditMode && goldenSignal.id)) && (
          <NameField form={form} isEditMode={isEditMode} />
        )}
        {(form.values.integration?.type?.toLowerCase() ===
          IntegrationType.Custom ||
          privateNetworkIntegrationAlways) &&
          isPrivateNetworkIntegrationEnabled && (
            <PrivateNetworkIntegrationCheckbox
              isEnabled={form.values?.integration?.isPrivateNetworkIntegration}
              name="integration.isPrivateNetworkIntegration"
              onChange={form.handleChange}
            />
          )}
        <FormControl fullWidth component="fieldset" variant="standard">
          <TextField
            fullWidth
            data-testid="service-status-check-url"
            error={resolveTouchState && Boolean(errors.integration?.url)}
            helperText={resolveTouchState && errors.integration?.url}
            label={computeURLabel(form.values.integration?.type)}
            name="integration.url"
            type="text"
            value={form.values.integration?.url}
            onBlur={form.handleBlur}
            onChange={(e) => {
              form.handleChange(e)
              setEndpointResponse(null)
            }}
          />
          <FormHelperText>
            <URLHelperText integration={form.values.integration} />
          </FormHelperText>
        </FormControl>
        {(!form.values.integration?.isConfigured ||
          form.values.integration?.type === IntegrationType.Custom) && (
          <ServiceGoldenSignalHeaders
            authenticationError={authenticationError}
            formPrefix="integration"
            isAuthenticationSaved={isAuthenticationSaved}
            isCustom={form.values.integration?.type === IntegrationType.Custom}
            isIntegrationConfigured={form.values.integration?.isConfigured}
            isSaveButtonDisabled={
              isServiceGoldenSignalHeadersSaveButtonDisabled
            }
            shouldShowAuthenticationButton={shouldShowAuthenticationButton}
            statusCheckId={goldenSignal?.id}
            onSaveAuthentication={handleSaveAuthentication}
          />
        )}
        {form.values.integration?.type === IntegrationType.Custom && (
          <>
            <TestTeamExternalIntegration
              authenticate={false}
              disabled={Boolean(form.errors?.integration)}
              integration={form.values.integration}
              statusCheckId={goldenSignal?.id}
              onError={() => {
                setEndpointResponse(null)
                setIsEvaluationChecked(false)
              }}
              onResponseReceived={(endpointResponse) => {
                setEndpointResponse(endpointResponse)
                setIsEvaluationChecked(false)
              }}
            />
            <TestEvaluationGoldenSignalIntegration
              endpointResponse={endpointResponse}
              onChanged={setIsEvaluationChecked}
            />
          </>
        )}
        <Stack alignItems="flex-start" spacing={2} sx={{ minWidth: '50vw' }}>
          {form.values.integration?.type !== IntegrationType.Custom && (
            <TestStatusCheck
              disabled={isTestStatusCheckButtonDisabled}
              evaluationConfiguration={{
                ...form.values.evaluationConfiguration,
                responseBodyEvaluation: computeResponseBodyEvaluation(
                  form.values.integration
                )
              }}
              integration={form.values.integration}
              onSuccess={(endpointResponse) => {
                setEndpointResponse(endpointResponse)
                setIsEvaluationChecked(true)
                const resName = getResourceNameFromResponseBody(
                  endpointResponse,
                  form.values.integration
                )
                if (resName != null) {
                  duplicateGoldenSignalName
                    ? form.setFieldValue(
                        'name',
                        duplicateGoldenSignalName,
                        true
                      )
                    : form.setFieldValue('name', resName, true)
                }
                setResourceName(resName)
              }}
            />
          )}
          {form.values.integration?.type !== IntegrationType.Custom &&
            endpointResponse &&
            !isEditMode &&
            !resourceName && <NameField form={form} isEditMode={isEditMode} />}
        </Stack>
        <Stack direction="row" pt={4} spacing={2}>
          <Button
            color="primary"
            data-testid="service-status-check-save"
            disabled={isSaveButtonDisabled}
            type="submit"
            variant="contained"
            onClick={form.submitForm}
          >
            Save
          </Button>
          <Button
            color="secondary"
            data-testid="add-edit-team-integration-dialog-cancel"
            variant="contained"
            onClick={onCanceled}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </FormikProvider>
  )
}
